import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FaCheck, FaTimes } from "react-icons/fa";
import { FiInfo, FiFilter, FiTrash2, FiDownload } from "react-icons/fi";
import { UserContext } from "../../Context";
import * as routes from "../../routes";
import { deleteBill, getBills, updateBillStatus } from "../../Services/BillService";
import { getSites } from "../../Services/siteService"
import { getAllUsers } from "../../Services/UserService";
import { BILL_POPUP_FIELDS, DEFAULT_STATUS_OPTIONS, MISC, TYPES_OPTIONS, EXCEL_FILE_COLUMNS, VALIDATION_MESSAGES } from "./constants"
import { CommonTable, Loader, DownloadExcelBill } from "../../Components/Common";
import BillCardView from "./Components/BillCardView";
import CommonFilter from "../../Components/Common/CommonFilter";
import {
    BILL_LABELS,
    ICON_TITLES,
    PAGE_TITLE,
    BUTTON_TEXT,
    TOAST_MESSAGES,
    BILL_COLUMNS,
    STATUS,
} from "./constants";
import { GET_ALL_PAGINATION, RESET_PAGINATION } from "../../Constants/Strings";
import SubMenuModal from "../Dashboard/Component/SubMenuModal";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { getTimestampForFilename } from "../../Utils/timeUtils";
import { formatDateDDMMYYYY } from "../../Utils";

const ViewBills = () => {
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState(RESET_PAGINATION);
    const [users, setUsers] = useState([]);
    // const [filters, setFilters] = useState({ status: "", user: "", date: "" });
    const location = useLocation();
    const [filters, setFilters] = useState(() => {
        return (
            location.state?.filters || {
                status: "",
                date: null,
                user: "",
                type: "",
                site: "",
            }
        );
    });
    const [selectedUserOption, setSelectedUserOption] = useState(() => {
        return (
            location.state?.selectedUser || null
        );
    });
    const [tempFilters, setTempFilters] = useState(filters);
    const [showFilter, setShowFilter] = useState(false);
    const navigate = useNavigate();
    const { user, billReviewAccess } = useContext(UserContext);
    const [showBillIcons, setShowBillIcons] = useState(false);
    const [siteOptions, setSiteOptions] = useState([])

    useEffect(() => {
        fetchBills({ offset: 0, limit: pagination.limit }, true, filters);
    }, [filters]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = (await getAllUsers(GET_ALL_PAGINATION)).data.results;
                const sites = (await getSites(GET_ALL_PAGINATION)).data.results;
                setSiteOptions(sites)
                setUsers(res);
            } catch (err) {
                const msg = extractErrorMessage(err) || TOAST_MESSAGES.USER_FETCH_ERROR;
                toast.error(msg);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    const fetchBills = async (params, isInitial = false, currentFilters = filters) => {
        setLoading(true);
        try {
            const cleanedFilters = Object.fromEntries(
                Object.entries(currentFilters).filter(([_, val]) => val !== "")
            );

            const res = await getBills({ ...params, ...cleanedFilters });

            if (!res || !res.data) {
                throw new Error(VALIDATION_MESSAGES.NO_RESPONSE_DATA);
            }

            const results = res.data.results || [];
            setBills((prev) => (isInitial ? results : [...prev, ...results]));
            setPagination((prev) => ({
                ...prev,
                count: res.data.count || 0,
                offset: isInitial ? results.length : prev.offset + results.length,
            }));
        } catch (err) {
            toast.error(TOAST_MESSAGES.BILLS_FETCH_ERROR);
            if (isInitial) {
                setBills([]);
                setPagination((prev) => ({ ...prev, count: 0, offset: 0 }));
            }
        } finally {
            setLoading(false);
        }
    };

    const loadMoreBills = () => {
        if (pagination.offset >= pagination.count) return;
        fetchBills({ offset: pagination.offset, limit: pagination.limit });
    };

    const handleStatusChange = async (id, status) => {
        try {
            const response = await updateBillStatus(id, { action: status });
            if (response.status === 200) {
                toast.success(TOAST_MESSAGES.STATUS_UPDATE_SUCCESS(status));
                setPagination(RESET_PAGINATION);
                fetchBills({ offset: 0, limit: RESET_PAGINATION.limit }, true);
            } else {
                const msg = extractErrorMessage(response) || TOAST_MESSAGES.BILL_STATUS_ERROR;
                toast.error(msg);
            }
        } catch (error) {
            const msg = extractErrorMessage(error) || TOAST_MESSAGES.BILL_STATUS_ERROR;
            toast.error(msg);
        }
    };

    const handleApplyFilters = () => {
        setSelectedUserOption(tempFilters.user);
        tempFilters.user = tempFilters.user?.id;
        setFilters(tempFilters);
        setPagination(RESET_PAGINATION);
        setBills([]);
        setShowFilter(false);

        // Update navigation state for back button
        navigate(".", { state: { filters: tempFilters, selectedUser: tempFilters.user }, replace: true });
    };

    const handleClearFilters = () => {
        const cleared = { status: "", user: "", date: "", type: "", site: "" };
        setTempFilters(cleared);
        setFilters(cleared);
        setPagination(RESET_PAGINATION);
        setBills([]);
        setSelectedUserOption(null);

        navigate(".", { state: { filters: cleared, selectedUser: null }, replace: true });
    };


    const handleCancelFilters = () => {
        setTempFilters(filters);
        setShowFilter(false);
    };

    const downloadExcelData = () => {
        DownloadExcelBill({
            fetchFn: getBills,
            fieldMappings: EXCEL_FILE_COLUMNS,
            fileName: `Bills_Data_${getTimestampForFilename()}.xlsx`,
        });
    };

    const handleDeleteBill = async (id) => {
        try {
            await deleteBill(id);
            toast.success(TOAST_MESSAGES.BILL_DELETE);

            setPagination(RESET_PAGINATION);
            const cleanedFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, val]) => val !== "")
            );

            const response = await getBills({
                offset: 0,
                limit: RESET_PAGINATION.limit,
                ...cleanedFilters,
            });

            setBills(response.data?.results || []);
            setPagination((prev) => ({
                ...prev,
                count: response.data.count,
                offset: response.data.results.length,
            }));
        } catch (error) {
            const msg = extractErrorMessage(error) || TOAST_MESSAGES.BILL_DELETE_ERROR;
            toast.error(msg);
        }
    };


    const BILL_COLUMN = billReviewAccess ? BILL_COLUMNS.ADMIN : BILL_COLUMNS.USER;

    const handleRenderField = (key, row) => {
        const isPending = row.status === "PENDING_L1_APPROVAL" || row.status === "PENDING_L2_APPROVAL";

        if (key === "site") return row.site_name || "-";
        if (key === "date") return formatDateDDMMYYYY(row.date);
        if (key === "category") return row.category_name || "-";
        if (key === "status") return row.status?.toLowerCase().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || "N/A";
        if (key === "user") return row.user_name || "-";
        if (key === "type") return row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1).toLowerCase() : "-";

        if (key === "actions") {
            return (
                <div
                    className={`grid ${billReviewAccess ? "grid-cols-4" : "grid-cols-2"} items-center justify-center gap-4 text-xl text-center`}
                >
                    <FiInfo
                        className="text-green-600 text-xl cursor-pointer"
                        onClick={() => navigate(routes.VIEW_BILL.replace(":billId", row.id), { state: { filters } })}
                        title={ICON_TITLES.VIEW}
                    />
                    {billReviewAccess && (
                        <>
                            <FaCheck
                                className={`cursor-pointer ${isPending ? "text-green-600" : "text-gray-400 cursor-not-allowed"}`}
                                onClick={() => isPending && handleStatusChange(row.id, STATUS.APPROVED)}
                                title={ICON_TITLES.APPROVE}
                            />
                            <FaTimes
                                className={`cursor-pointer ${isPending ? "text-red-600" : "text-gray-400 cursor-not-allowed"}`}
                                onClick={() => isPending && handleStatusChange(row.id, STATUS.REJECTED)}
                                title={ICON_TITLES.REJECT}
                            />
                        </>
                    )}
                    <FiTrash2
                        className={`cursor-pointer ${isPending ? "text-red-600" : "text-gray-400 cursor-not-allowed"}`}
                        onClick={() => {
                            if (!isPending) return;

                            const confirmed = window.confirm(TOAST_MESSAGES.DELETE_ALERT);
                            if (confirmed) {
                                handleDeleteBill(row.id)
                            }
                        }}
                        title={ICON_TITLES.DELETE}
                    />
                </div>
            );
        }
        return row[key] || "-";
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-primary">{PAGE_TITLE}</h2>

                <div className="flex gap-2 items-center">
                    <button
                        className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
                        onClick={() => downloadExcelData()}
                    >
                        <FiDownload className="text-xl" />

                    </button>

                    <button
                        className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
                        onClick={() => {
                            setTempFilters(filters);
                            setShowFilter(true);
                        }}
                    >
                        <FiFilter className="text-xl" />
                    </button>

                    <button
                        onClick={() => setShowBillIcons(true)}
                        className="bg-primary text-white px-4 py-2 rounded-md hover:bg-secondary text-sm"
                    >
                        {BUTTON_TEXT.ADD_BILL}
                    </button>
                </div>
            </div>



            {/* Filter Modal */}
            {showFilter && (
                <CommonFilter
                    title="Bill Filter"
                    tempFilters={tempFilters}
                    setTempFilters={setTempFilters}
                    onApply={handleApplyFilters}
                    onClear={handleClearFilters}
                    onCancel={handleCancelFilters}
                    userOptions={users}
                    statusOptions={DEFAULT_STATUS_OPTIONS}
                    siteOptions={siteOptions}
                    typeOptions={TYPES_OPTIONS}
                    date={true}
                    useSearchableUser={true}
                    selectedSearchableValue={selectedUserOption}
                />
            )}

            {/* Loader or Data */}
            {loading && bills.length === 0 ? (
                <div className="flex justify-center pt-10">
                    <Loader size={4} />
                </div>
            ) : bills.length === 0 ? (
                <div className="text-center text-gray-500 mt-10">{MISC.NO_BILLS_FOUND}</div>
            ) : (
                <>
                    {/* Desktop View */}
                    <div className="hidden md:block">
                        <InfiniteScroll
                            dataLength={bills.length}
                            next={loadMoreBills}
                            hasMore={pagination.offset < pagination.count}
                            scrollableTarget="desktopBills"
                        >
                            <div id="desktopBills" className="h-[68vh] overflow-y-auto border rounded-md">
                                <CommonTable
                                    tableColumns={BILL_COLUMN}
                                    tableRows={bills}
                                    columnLabels={BILL_LABELS}
                                    renderField={handleRenderField}
                                />
                            </div>
                        </InfiniteScroll>
                    </div>

                    {/* Mobile View */}
                    <div className="block md:hidden">
                        <InfiniteScroll
                            dataLength={bills.length}
                            next={loadMoreBills}
                            hasMore={pagination.offset < pagination.count}
                            scrollableTarget="mobileBills"
                        >
                            <div id="mobileBills" className="h-[70vh] overflow-y-auto">
                                <BillCardView bills={bills} currentUserRole={user?.role} />
                            </div>
                        </InfiniteScroll>
                    </div>
                </>
            )}

            {showBillIcons && (
                <SubMenuModal
                    subMenuFields={BILL_POPUP_FIELDS}
                    setShowPopup={setShowBillIcons}
                    title={BILL_POPUP_FIELDS?.title}
                />
            )}
        </div>
    );
};

export default ViewBills;
